import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { Menu, Plus, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Trash2, Calendar, User, Mail, Phone, Users, Briefcase, DollarSign, SlidersHorizontal, MapPin, Printer, Smartphone, AlertTriangle, StickyNote, Send, CheckCircle2, PauseCircle, Loader2, Pencil, UserPlus, Star, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { Sidebar } from '../components/Sidebar';
import { INITIAL_JOBS } from './Jobs';
import { useScreenInit } from '../useScreenInit';
// --- Types & Mock Data ---
type Note = {
  id: string;
  body: string;
  author: string;
  createdAt: string;
};
type ProjectStatus = 'active' | 'completed' | 'on_hold';
type Project = {
  id: string;
  name: string;
  ref: string;
  status: ProjectStatus;
  value: number;
  startDate: string;
  endDate?: string;
};
type Contact = {
  id: string;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary?: boolean;
  isKeyContact?: boolean;
};
type Client = {
  id: string;
  ref: string;
  name: string;
  dateCreated: string;
  projectCount: number;
  totalRevenue: number;
  lastProjectDate?: string;
  // Address
  street?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  // Contact
  phone?: string;
  fax?: string;
  mobile?: string;
  email?: string;
  // Notes
  notes?: Note[];
  // Projects
  projects?: Project[];
  // Contacts
  contacts?: Contact[];
};
const INITIAL_CLIENTS: Client[] = [{
  id: '1',
  ref: 'CL-0001',
  name: 'Atlas Property Holdings',
  dateCreated: '2023-11-15',
  projectCount: 12,
  totalRevenue: 485000,
  lastProjectDate: '2026-05-22',
  email: 'sarah@atlas.com',
  phone: '(555) 123-4567',
  notes: [{
    id: 'n1',
    body: 'Key account — quarterly review meeting set for the first week of June. Sarah is the primary contact for all scoping conversations.',
    author: 'Matthew Carter',
    createdAt: '2026-05-20T14:32:00Z'
  }, {
    id: 'n2',
    body: 'Renewed master agreement through Q4 2027. Updated billing terms to net-30.',
    author: 'Matthew Carter',
    createdAt: '2026-04-02T09:10:00Z'
  }],
  projects: [{
    id: 'p1',
    ref: 'PR-1041',
    name: 'Midtown Tower Refurb',
    status: 'active',
    value: 142000,
    startDate: '2026-03-01'
  }, {
    id: 'p2',
    ref: 'PR-1029',
    name: 'Brooklyn Mixed-Use Survey',
    status: 'active',
    value: 88500,
    startDate: '2026-04-12'
  }, {
    id: 'p3',
    ref: 'PR-0987',
    name: 'Lexington Ave Façade',
    status: 'completed',
    value: 64200,
    startDate: '2025-09-08',
    endDate: '2026-01-22'
  }, {
    id: 'p4',
    ref: 'PR-0951',
    name: 'Hudson Yards Fit-out',
    status: 'on_hold',
    value: 41000,
    startDate: '2025-11-02'
  }],
  contacts: [{
    id: 'c1',
    name: 'Sarah Mitchell',
    role: 'Primary Contact',
    email: 'sarah@atlas.com',
    phone: '(555) 123-4567',
    mobile: '(555) 998-2210',
    isPrimary: true
  }, {
    id: 'c2',
    name: 'David Reyes',
    role: 'Accounts Payable',
    email: 'accounts@atlas.com',
    phone: '(555) 123-4570'
  }, {
    id: 'c3',
    name: 'Priya Patel',
    role: 'Project Manager',
    email: 'priya.p@atlas.com',
    mobile: '(555) 776-3309'
  }]
}, {
  id: '2',
  ref: 'CL-0002',
  name: 'Beacon Real Estate',
  dateCreated: '2023-12-02',
  projectCount: 8,
  totalRevenue: 312500,
  lastProjectDate: '2026-04-10',
  email: 'mchang@beaconre.com',
  notes: [{
    id: 'n3',
    body: 'Michael is currently on parental leave until July — route urgent items to assistant.',
    author: 'Matthew Carter',
    createdAt: '2026-05-15T11:00:00Z'
  }],
  projects: [{
    id: 'p5',
    ref: 'PR-1012',
    name: 'River North Lofts',
    status: 'active',
    value: 96500,
    startDate: '2026-02-18'
  }, {
    id: 'p6',
    ref: 'PR-0978',
    name: 'Lincoln Park Condos',
    status: 'completed',
    value: 124000,
    startDate: '2025-08-14',
    endDate: '2026-04-10'
  }],
  contacts: [{
    id: 'c4',
    name: 'Michael Chang',
    role: 'Primary Contact',
    email: 'mchang@beaconre.com',
    mobile: '(555) 312-8841',
    isPrimary: true
  }, {
    id: 'c5',
    name: 'Karen Sloane',
    role: 'Operations',
    email: 'k.sloane@beaconre.com'
  }]
}, {
  id: '3',
  ref: 'CL-0003',
  name: 'Crestview Developments',
  dateCreated: '2024-01-10',
  projectCount: 15,
  totalRevenue: 627000,
  lastProjectDate: '2026-05-30',
  phone: '(555) 987-6543',
  projects: [{
    id: 'p7',
    ref: 'PR-1055',
    name: 'Sunset Heights Phase II',
    status: 'active',
    value: 215000,
    startDate: '2026-01-15'
  }, {
    id: 'p8',
    ref: 'PR-1020',
    name: 'Venice Boardwalk Retail',
    status: 'active',
    value: 178000,
    startDate: '2026-03-04'
  }, {
    id: 'p9',
    ref: 'PR-0944',
    name: 'Pasadena HQ Renovation',
    status: 'completed',
    value: 134000,
    startDate: '2025-07-20',
    endDate: '2026-02-28'
  }],
  contacts: [{
    id: 'c6',
    name: 'Lena Ortiz',
    role: 'Director of Development',
    email: 'lena@crestview.dev',
    phone: '(555) 987-6543',
    mobile: '(555) 887-1290',
    isPrimary: true
  }]
}, {
  id: '4',
  ref: 'CL-0004',
  name: 'Meridian Capital Group',
  dateCreated: '2024-02-28',
  projectCount: 6,
  totalRevenue: 198000,
  lastProjectDate: '2026-02-18',
  email: 'david.s@meridian.com'
}, {
  id: '5',
  ref: 'CL-0005',
  name: 'Pinnacle Investments',
  dateCreated: '2024-03-15',
  projectCount: 9,
  totalRevenue: 356400,
  lastProjectDate: '2026-03-25'
}, {
  id: '6',
  ref: 'CL-0006',
  name: 'Summit Commercial',
  dateCreated: '2024-04-05',
  projectCount: 4,
  totalRevenue: 142800,
  lastProjectDate: '2025-11-14'
}, {
  id: '7',
  ref: 'CL-0007',
  name: 'Vanguard Properties',
  dateCreated: '2024-04-20',
  projectCount: 11,
  totalRevenue: 421300,
  lastProjectDate: '2026-05-08',
  email: 'info@vanguardprop.com',
  projects: [{
    id: 'p10',
    ref: 'PR-1048',
    name: 'Magnificent Mile Tower',
    status: 'active',
    value: 198000,
    startDate: '2026-02-01'
  }, {
    id: 'p11',
    ref: 'PR-0995',
    name: 'Wicker Park Lofts',
    status: 'on_hold',
    value: 72000,
    startDate: '2025-10-15'
  }]
}, {
  id: '8',
  ref: 'CL-0008',
  name: 'Horizon Realty Trust',
  dateCreated: '2024-05-12',
  projectCount: 7,
  totalRevenue: 268900,
  lastProjectDate: '2026-01-30',
  phone: '(555) 456-7890'
}];
type SortField = 'name' | 'dateCreated' | 'projectCount' | 'totalRevenue' | 'lastProjectDate';
type SortOrder = 'asc' | 'desc';
const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
const formatCurrencyCompact = (value: number) => {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${(value / 1000).toFixed(0)}K`;
  return `£${value}`;
};
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const hasActiveProject = (client: Client) => (client.projects || []).some(p => p.status === 'active');
export function Clients() {
  useScreenInit();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>(INITIAL_CLIENTS);
  const referredClientNames = useMemo(() => new Set(INITIAL_JOBS.filter(job => job.referralMethod === 'Referred by client' && job.referringClient).map(job => job.referringClient)), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Auth: in a real app this would come from an auth context.
  // Admins are the only role allowed to delete clients.
  const [isAdmin] = useState(true);
  // KPI Totals (across all clients, not just filtered)
  const totals = useMemo(() => ({
    clients: clients.length,
    projects: clients.reduce((sum, c) => sum + c.projectCount, 0),
    revenue: clients.reduce((sum, c) => sum + c.totalRevenue, 0)
  }), [clients]);
  const filteredAndSortedClients = useMemo(() => {
    let result = [...clients];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q) || c.ref.toLowerCase().includes(q));
    }
    if (filterStatus !== 'all') {
      result = result.filter(c => filterStatus === 'active' ? hasActiveProject(c) : !hasActiveProject(c));
    }
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') comparison = a.name.localeCompare(b.name);else if (sortField === 'dateCreated') comparison = new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();else if (sortField === 'projectCount') comparison = a.projectCount - b.projectCount;else if (sortField === 'totalRevenue') comparison = a.totalRevenue - b.totalRevenue;else if (sortField === 'lastProjectDate') comparison = new Date(a.lastProjectDate || a.dateCreated).getTime() - new Date(b.lastProjectDate || b.dateCreated).getTime();
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [clients, searchQuery, filterStatus, sortField, sortOrder]);
  // Reset to page 1 when filters/search/page size change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterStatus, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedClients.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedClients = filteredAndSortedClients.slice(pageStart, pageStart + pageSize);
  const showingFrom = filteredAndSortedClients.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, filteredAndSortedClients.length);
  const exportClients = () => {
    downloadCsv('clients-export.csv', [{
      label: 'Client ref',
      value: client => client.ref
    }, {
      label: 'Client name',
      value: client => client.name
    }, {
      label: 'Status',
      value: client => hasActiveProject(client) ? 'Active' : 'Inactive'
    }, {
      label: 'Projects',
      value: client => client.projectCount
    }, {
      label: 'Revenue',
      value: client => client.totalRevenue
    }, {
      label: 'Last project date',
      value: client => client.lastProjectDate
    }, {
      label: 'Date created',
      value: client => client.dateCreated
    }, {
      label: 'Referred us',
      value: client => referredClientNames.has(client.name) ? 'Yes' : 'No'
    }, {
      label: 'Email',
      value: client => client.email
    }, {
      label: 'Phone',
      value: client => client.phone
    }, {
      label: 'Mobile',
      value: client => client.mobile
    }, {
      label: 'Street',
      value: client => client.street
    }, {
      label: 'Line 2',
      value: client => client.line2
    }, {
      label: 'City',
      value: client => client.city
    }, {
      label: 'County',
      value: client => client.county
    }, {
      label: 'Postcode',
      value: client => client.postcode
    }], filteredAndSortedClients);
  };
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const handleSortSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, order] = e.target.value.split('-') as [SortField, SortOrder];
    setSortField(field);
    setSortOrder(order);
  };
  const openNewModal = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };
  const handleSaveClient = (clientData: Partial<Client>) => {
    if (editingClient) {
      setClients(clients.map(c => c.id === editingClient.id ? {
        ...c,
        ...clientData
      } as Client : c));
    } else {
      const newClient: Client = {
        ...clientData,
        id: Math.random().toString(36).substr(2, 9),
        ref: `CL-${String(clients.length + 1).padStart(4, '0')}`,
        dateCreated: new Date().toISOString().split('T')[0],
        projectCount: 0,
        totalRevenue: 0
      } as Client;
      setClients([newClient, ...clients]);
    }
    setIsModalOpen(false);
  };
  const handleDeleteClient = (id: string) => {
    setClients(clients.filter(c => c.id !== id));
    setIsModalOpen(false);
  };
  const handleAddNote = (clientId: string, body: string) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    const newNote: Note = {
      id: Math.random().toString(36).substr(2, 9),
      body: trimmed,
      author: 'Matthew Carter',
      createdAt: new Date().toISOString()
    };
    setClients(prev => prev.map(c => c.id === clientId ? {
      ...c,
      notes: [newNote, ...(c.notes || [])]
    } : c));
    // Keep the editing client in sync so the drawer sees the new note immediately
    setEditingClient(prev => prev && prev.id === clientId ? {
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    } : prev);
  };
  const toggleKeyContact = (clientId: string, contactId: string) => {
    const updateContacts = (contacts: Contact[] = []) => contacts.map(contact => contact.id === contactId ? {
      ...contact,
      isKeyContact: !contact.isKeyContact
    } : contact);
    setClients(prev => prev.map(client => client.id === clientId ? {
      ...client,
      contacts: updateContacts(client.contacts)
    } : client));
    setEditingClient(prev => prev && prev.id === clientId ? {
      ...prev,
      contacts: updateContacts(prev.contacts)
    } : prev);
  };
  const handleAddContact = (clientId: string, contact: Omit<Contact, 'id'>) => {
    const newContact: Contact = {
      ...contact,
      id: Math.random().toString(36).substr(2, 9)
    };
    setClients(prev => prev.map(c => {
      if (c.id !== clientId) return c;
      // If new contact is primary, demote others
      const existing = (c.contacts || []).map(ct => newContact.isPrimary ? {
        ...ct,
        isPrimary: false
      } : ct);
      return {
        ...c,
        contacts: [...existing, newContact]
      };
    }));
    setEditingClient(prev => {
      if (!prev || prev.id !== clientId) return prev;
      const existing = (prev.contacts || []).map(ct => newContact.isPrimary ? {
        ...ct,
        isPrimary: false
      } : ct);
      return {
        ...prev,
        contacts: [...existing, newContact]
      };
    });
  };
  return <div className="flex h-screen w-screen overflow-hidden bg-[#1F5C9E] font-sans" data-id="element-618" data-mp="m8joot8kva40">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-619" data-mp="89am4wdaoeii" />

      <main className="flex-1 min-w-0 w-full p-0 md:py-3 md:pr-3 md:pl-0 overflow-hidden flex" data-id="element-620" data-mp="8b501qq5f6d3">
        <div className="flex-1 min-w-0 w-full bg-white md:rounded-2xl shadow-xl flex flex-col overflow-x-hidden overflow-y-auto" data-id="element-621" data-mp="da81z2au0wgu">
          {/* Header */}
          <div className="p-5 md:p-8 md:pb-6 border-b border-slate-100" data-id="element-622" data-mp="3azb4ib9jxyp">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden flex items-center gap-2 text-[#14466B] mb-4 hover:opacity-80 transition-opacity" aria-label="Open menu" data-id="element-623" data-mp="3lo6u1suonxp2">
              <Menu className="w-6 h-6" data-id="element-624" data-mp="3br2be301qkd0" />
            </button>

            <div data-id="element-625" data-mp="3gpdiw72vcis2">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-626" data-mp="3gnsmebwzuprk">Clients</h1>
              <p className="text-slate-500 text-sm mt-1" data-id="element-627" data-mp="2x2iomg71w2zd">
                Manage GCA's clients and their details.
              </p>
            </div>
          </div>

          {/* Search + Actions Row */}
          <div className="p-5 md:p-8 pb-4" data-id="element-628" data-mp="371rg92eg15ik">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-id="element-629" data-mp="372jc8ebyd59d">
              <div className="relative flex-1" data-id="element-630" data-mp="320k8v4g7q8cf">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-631" data-mp="3g1objmvc92ml" />
                <input type="text" placeholder="Search clients..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 placeholder:text-slate-400" data-id="element-632" data-mp="3c0kx94sxvenh" />
              </div>

              <FilterPopover filterStatus={filterStatus} setFilterStatus={setFilterStatus} sortField={sortField} sortOrder={sortOrder} onSortChange={(field, order) => {
              setSortField(field);
              setSortOrder(order);
            }} data-id="element-633" data-mp="3uopv5vmx05ed" />

              <button type="button" onClick={exportClients} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export filtered clients as CSV" data-id="element-634" data-mp="2uaphy63k098m"><Download className="h-4 w-4" data-id="element-635" data-mp="26kv9e0q9p6u6" /><span className="hidden lg:inline" data-id="element-636" data-mp="2bcwgl88kw7dc">Export CSV</span></button>
              <button onClick={openNewModal} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] text-white text-sm font-medium transition-colors whitespace-nowrap shadow-sm" data-id="element-637" data-mp="2be7onk9241a9">
                <Plus className="w-4 h-4" data-id="element-638" data-mp="69vrb3ahul" />
                New client
              </button>
            </div>
          </div>

          {/* Table Area */}
          <div className="flex-1 flex flex-col px-5 md:px-8 pb-8 md:pb-10 min-h-0" data-id="element-639" data-mp="3n5x4tudjt4nn">
            {filteredAndSortedClients.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-640" data-mp="3i6asro9ghugf">
                <Users className="w-12 h-12 text-slate-300 mb-4" data-id="element-641" data-mp="38gr8ce63hsjg" />
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-642" data-mp="38f6giwb60npa">
                  No clients found
                </h3>
                <p className="text-slate-500 text-sm max-w-sm" data-id="element-643" data-mp="2zgun6miwzbt5">
                  We couldn't find any clients matching your current filters.
                  Try adjusting your search or adding a new client.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setFilterStatus('all');
            }} className="mt-4 text-[#14466B] text-sm font-medium hover:underline" data-id="element-644" data-mp="2pjgdd5rhxl9t">
                  Clear filters
                </button>
              </div> : <>
                {/* Desktop Table */}
                <div className="hidden md:block flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl" data-id="element-645" data-mp="2a9r8aaa370kz">
                  <table className="w-full text-left border-collapse" data-id="element-646" data-mp="25dalpkijcua0">
                    <thead className="sticky top-0 z-10" data-id="element-647" data-mp="20jpnpsgy00at">
                      <tr className="bg-slate-50 border-b border-slate-200" data-id="element-648" data-mp="1vl4u4bw74evc">
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24" data-id="element-649" data-mp="1vj1v52d2fbeh">
                          Ref
                        </th>
                        <SortableHeader label="Name" field="name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-650" data-mp="1mjwy60h5jq4f" />
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-28" data-id="element-651" data-mp="214ayp2r6hk9">
                          Status
                        </th>
                        <SortableHeader label="Projects" field="projectCount" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-28" data-id="element-652" data-mp="b2aul70p88hq" />
                        <SortableHeader label="Revenue" field="totalRevenue" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-653" data-mp="3jh2n6gsx12zn" />
                        <SortableHeader label="Last Project" field="lastProjectDate" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-36" data-id="element-654" data-mp="2ykucke6rzux" />
                        <SortableHeader label="Date Created" field="dateCreated" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-40" data-id="element-655" data-mp="7qmf6upf6ywk" />
                        <th className="w-32 py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-656" data-mp="3k7hphauwfu87">Referred us</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-657" data-mp="zh0n8aar5h0b">
                      {paginatedClients.map(client => <tr key={client.id} onClick={() => openEditModal(client)} className="hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-658" data-mp="17xrixm00wdf8">
                          <td className="py-3 px-4 text-sm text-slate-500 font-medium" data-id="element-659" data-mp="y26c9ak0eoz5">
                            {client.ref}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900 font-medium group-hover:text-[#14466B] transition-colors" data-id="element-660" data-mp="1qpts6cac4rfg">
                            {client.name}
                          </td>
                          <td className="py-3 px-4" data-id="element-661" data-mp="1gxwcotcandax">
                            <StatusPill active={hasActiveProject(client)} data-id="element-662" data-mp="1lxisnajib7nf" />
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700 font-medium" data-id="element-663" data-mp="29muzi3rd33mk">
                            {client.projectCount}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900 font-semibold tabular-nums" data-id="element-664" data-mp="2efwhwlqs1law">
                            {formatCurrency(client.totalRevenue)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600" data-id="element-665" data-mp="lt1j8e78g510">
                            {client.lastProjectDate ? new Date(client.lastProjectDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : <span className="text-slate-300" data-id="element-666" data-mp="zo3msuolxian">—</span>}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500" data-id="element-667" data-mp="1s79456q9nbjn">
                            {new Date(client.dateCreated).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                          </td>
                          <td className="py-3 px-4" data-id="element-668" data-mp="24rpnn5oe0lij">
                            {referredClientNames.has(client.name) ? <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-700" title="Referred us" data-id="element-669" data-mp="29md6op0mgnpq"><Star className="h-3.5 w-3.5 fill-current" aria-hidden="true" data-id="element-670" data-mp="2iouomr98uu6x" /><span className="sr-only" data-id="element-671" data-mp="2in1j5fadag0i">Referred us</span></span> : <span className="text-slate-300" data-id="element-672" data-mp="2nfkwcb55794s">—</span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3" data-id="element-673" data-mp="31flw1n9o8aio">
                  {paginatedClients.map(client => <div key={client.id} onClick={() => openEditModal(client)} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm active:bg-slate-50 transition-colors" data-id="element-674" data-mp="12am60k507isn">
                      <div className="flex justify-between items-start mb-3" data-id="element-675" data-mp="1b5a7dz7gkgc6">
                        <h3 className="font-semibold text-slate-900 text-sm" data-id="element-676" data-mp="16aw8hmtl7r2r">
                          {client.name}
                        </h3>
                        <span className="text-xs font-medium text-slate-400" data-id="element-677" data-mp="1g2isa7jzqyh4">
                          {client.ref}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3 flex-wrap" data-id="element-678" data-mp="1tzpc85ggb37x">
                        <div className="flex items-center gap-1" data-id="element-679" data-mp="1pylxjma53d8a">
                          <Calendar className="w-3.5 h-3.5" data-id="element-680" data-mp="3vtwww8qhm7r2" />
                          {new Date(client.dateCreated).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100" data-id="element-681" data-mp="3hoejd2yqomne">
                        <div data-id="element-682" data-mp="3lo6lawtmnujs">
                          <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-683" data-mp="3lrau67i1fegs">
                            Projects
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-684" data-mp="3cvle8dzczcas">
                            {client.projectCount}
                          </div>
                        </div>
                        <div data-id="element-685" data-mp="330jp3i532a87">
                          <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-686" data-mp="32wm38or4f4km">
                            Revenue
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-687" data-mp="2tytvc5nqaz79">
                            {formatCurrency(client.totalRevenue)}
                          </div>
                        </div>
                        <div data-id="element-688" data-mp="s65prpnm3zob">
                          <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-689" data-mp="20rcuc5yy4nqa">
                            Last Project
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-690" data-mp="1vum6gwgwgnub">
                            {client.lastProjectDate ? new Date(client.lastProjectDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      }) : '—'}
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>

                {/* Pagination */}
                <Pagination page={safePage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} setPage={setPage} showingFrom={showingFrom} showingTo={showingTo} total={filteredAndSortedClients.length} data-id="element-691" data-mp="32ff1enox2zi0" />
              </>}
          </div>
        </div>
      </main>

      <AnimatePresence data-id="element-692" data-mp="xyqwgxlsrvqc">
        {isModalOpen && <ClientDrawer client={editingClient} isAdmin={isAdmin} onClose={() => setIsModalOpen(false)} onSave={handleSaveClient} onAddNote={editingClient ? body => handleAddNote(editingClient.id, body) : undefined} onAddContact={editingClient ? c => handleAddContact(editingClient.id, c) : undefined} onToggleKeyContact={editingClient ? contactId => toggleKeyContact(editingClient.id, contactId) : undefined} onDelete={editingClient && isAdmin ? () => handleDeleteClient(editingClient.id) : undefined} data-id="element-693" data-mp="xxgye8ey2njp" />}
      </AnimatePresence>
    </div>;
}
// --- KPI Card ---
type KpiCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: string;
};
function KpiCard({
  icon,
  label,
  value,
  accent
}: KpiCardProps) {
  return <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4" data-id="element-694" data-mp="1yxy7hnvgsdtz">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`} data-id="element-695" data-mp="231492i7mtgu4">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-696" data-mp="1p2whkptd77qc">
        <div className="text-xs text-slate-500 font-medium uppercase tracking-wide truncate" data-id="element-697" data-mp="1tyuzpvo3ntib">
          {label}
        </div>
        <div className="text-xl font-bold text-slate-900 tabular-nums" data-id="element-698" data-mp="3jfvzwqp4qg1b">
          {value}
        </div>
      </div>
    </div>;
}
// --- Status Pill ---
function StatusPill({
  active
}: {
  active: boolean;
}) {
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-699" data-mp="1cqls4l8x13g4">
      <span className="relative flex w-1.5 h-1.5" aria-hidden data-id="element-700" data-mp="1mhgbnhus1oxv">
        {active && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-701" data-mp="1hnu9ijm8p3gb" />}
        <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-702" data-mp="1viwkhl5y1izz" />
      </span>
      {active ? 'Active' : 'Inactive'}
    </span>;
}
// --- Sortable Table Header ---
type SortableHeaderProps = {
  label: string;
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
};
function SortableHeader({
  label,
  field,
  sortField,
  sortOrder,
  onSort,
  className = ''
}: SortableHeaderProps) {
  const isActive = sortField === field;
  return <th className={`py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors group ${className}`} onClick={() => onSort(field)} aria-sort={isActive ? sortOrder === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-703" data-mp="vru8f8h8775e">
      <div className="flex items-center gap-1" data-id="element-704" data-mp="19nfub6s5xatl">
        {label}
        {isActive ? sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 text-[#14466B]" data-id="element-705" data-mp="1iq8j93dvhag9" /> : <ChevronDown className="w-3 h-3 text-[#14466B]" data-id="element-706" data-mp="1io48q2lgxd70" /> : <ChevronDown className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-707" data-mp="1nja3zs0rg47r" />}
      </div>
    </th>;
}
// --- Client Drawer (right-side slide-out) ---
type ClientDrawerProps = {
  client: Client | null;
  isAdmin: boolean;
  onClose: () => void;
  onSave: (data: Partial<Client>) => void;
  onAddNote?: (body: string) => void;
  onAddContact?: (contact: Omit<Contact, 'id'>) => void;
  onToggleKeyContact?: (contactId: string) => void;
  onDelete?: () => void;
};
function ClientDrawer({
  client,
  isAdmin,
  onClose,
  onSave,
  onAddNote,
  onAddContact,
  onToggleKeyContact,
  onDelete
}: ClientDrawerProps) {
  const isEdit = !!client;
  const [mode, setMode] = useState<'view' | 'edit'>(isEdit ? 'view' : 'edit');
  const [activeTab, setActiveTab] = useState<'details' | 'notes' | 'contacts' | 'projects'>('details');
  const [formData, setFormData] = useState({
    name: client?.name || '',
    street: client?.street || '',
    line2: client?.line2 || '',
    city: client?.city || '',
    county: client?.county || '',
    postcode: client?.postcode || '',
    phone: client?.phone || '',
    fax: client?.fax || '',
    mobile: client?.mobile || '',
    email: client?.email || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newNote, setNewNote] = useState('');
  // Close drawer on Escape (only when confirm dialog isn't open)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDelete) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, confirmDelete]);
  const handleField = (key: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    if (errors[key]) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Client name is required';
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(formData);
    if (isEdit) setMode('view');
  };
  // Reset to formData snapshot of current client when bouncing in/out of edit mode
  const handleCancelEdit = () => {
    if (isEdit && client) {
      setFormData({
        name: client.name || '',
        street: client.street || '',
        line2: client.line2 || '',
        city: client.city || '',
        county: client.county || '',
        postcode: client.postcode || '',
        phone: client.phone || '',
        fax: client.fax || '',
        mobile: client.mobile || '',
        email: client.email || ''
      });
      setErrors({});
      setMode('view');
    } else {
      onClose();
    }
  };
  const handleSubmitNote = () => {
    if (!onAddNote || !newNote.trim()) return;
    onAddNote(newNote);
    setNewNote('');
  };
  // Always land on view when switching back to the Details tab for an existing client
  const goToDetails = () => {
    setActiveTab('details');
    if (isEdit) setMode('view');
  };
  const notes = client?.notes || [];
  const projects = client?.projects || [];
  const contacts = client?.contacts || [];
  const isDetails = activeTab === 'details';
  const showForm = isDetails && mode === 'edit';
  const showView = isDetails && mode === 'view' && isEdit;
  return <div className="fixed inset-0 z-[100] flex justify-end" data-id="element-708" data-mp="2akd7kss0e8wh">
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-709" data-mp="2fioiz7xycrgt" />

      {/* Drawer panel */}
      <motion.aside initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'tween',
      duration: 0.28,
      ease: [0.32, 0.72, 0, 1]
    }} className="relative w-full sm:max-w-xl bg-white shadow-2xl flex flex-col h-full" role="dialog" aria-modal="true" aria-label={isEdit ? client.name : 'New client'} data-id="element-710" data-mp="1rt3mdm8vlvp2">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-100 gap-4" data-id="element-711" data-mp="1adragm1lvwcj">
          <div className="min-w-0" data-id="element-712" data-mp="15en40362gcpw">
            {isEdit && mode === 'view' ? <>
                <h2 className="text-xl font-bold text-slate-900 truncate" data-id="element-713" data-mp="10jr28bb12azl">
                  {client.name}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-714" data-mp="rlf8i0ev6x2x">
                  <span className="font-medium text-slate-600" data-id="element-715" data-mp="rj2kp6mfdshy">
                    {client.ref}
                  </span>
                </p>
              </> : <>
                <h2 className="text-xl font-bold text-slate-900" data-id="element-716" data-mp="hnrdd9dcjnk8">
                  {isEdit ? 'Edit client' : 'New client'}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-717" data-mp="2aja9qszk1q0q">
                  {isEdit ? `${client.ref} · Update details for ${client.name}.` : 'Add a new client to your workspace.'}
                </p>
              </>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-718" data-mp="1mtfygfuvw9il">
            {isEdit && mode === 'view' && isDetails && <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#14466B] bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors" aria-label="Edit client" data-id="element-719" data-mp="1rk65rymvemfx">
                <Pencil className="w-3.5 h-3.5" data-id="element-720" data-mp="13vd5xr34nug1" />
                Edit
              </button>}
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close drawer" data-id="element-721" data-mp="18s3jo7rgmdya">
              <X className="w-5 h-5" data-id="element-722" data-mp="kamml23mldx4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 border-b border-slate-100" data-id="element-723" data-mp="z3g22niwdu1s">
          <TabButton active={activeTab === 'details'} onClick={goToDetails} icon={<User className="w-4 h-4" data-id="element-725" data-mp="184e81bcwcbn0" />} label="Details" data-id="element-724" data-mp="u5eim1hk7sps" />
          <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} icon={<StickyNote className="w-4 h-4" data-id="element-727" data-mp="1hyd5vy5xt995" />} label="Notes" count={notes.length} data-id="element-726" data-mp="136uukdo0w2w4" />
          <TabButton active={activeTab === 'contacts'} onClick={() => setActiveTab('contacts')} icon={<Users className="w-4 h-4" data-id="element-729" data-mp="1mw6c2r78q39f" />} label="Contacts" count={contacts.length} data-id="element-728" data-mp="1qwymvovrgg7t" />
          <TabButton active={activeTab === 'projects'} onClick={() => setActiveTab('projects')} icon={<Briefcase className="w-4 h-4" data-id="element-731" data-mp="1pnnu31nm8q26" />} label="Projects" count={projects.length} data-id="element-730" data-mp="1ulz1l5qfuoh8" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-id="element-732" data-mp="1zjqtw0uzwcly">
          {showView && client ? <ClientDetailsView client={client} data-id="element-733" data-mp="2davzdjl1nz47" /> : showForm ? <form id="client-form" onSubmit={handleSubmit} className="p-6 space-y-8" data-id="element-734" data-mp="2dd8cnjfuxlax">
              {/* Details section */}
              <section className="space-y-4" data-id="element-735" data-mp="2n7iju79gko5p">
                <SectionHeader title="Details" data-id="element-736" data-mp="2i9yr62c0k8u8" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-737" data-mp="2iba0sis54at9">
                  {isEdit && <Field label="Ref" className="sm:col-span-1" data-id="element-738" data-mp="sxockhphmh7g">
                      <input type="text" disabled value={client.ref} className="w-full px-4 py-2.5 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500 cursor-not-allowed" data-id="element-739" data-mp="nxshx92ok24k" />
                    </Field>}

                  <Field label="Name" required error={errors.name} className={isEdit ? 'sm:col-span-1' : 'sm:col-span-2'} data-id="element-740" data-mp="2usuhaezk45wu">
                    <input type="text" value={formData.name} onChange={e => handleField('name', e.target.value)} className={inputClass(!!errors.name)} placeholder="e.g. Atlas Property Holdings" data-id="element-741" data-mp="2kv6k7iehambi" />
                  </Field>
                </div>
              </section>

              {/* Address section — unchanged */}
              <section className="space-y-4" data-id="element-742" data-mp="a0sn7wkhcnus">
                <SectionHeader title="Address" icon={<MapPin className="w-4 h-4" data-id="element-744" data-mp="3mmtji9t8g9e" />} data-id="element-743" data-mp="8lzqivueb3pp" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-745" data-mp="3lbo7e0pv0g6">
                  <Field label="Street" className="sm:col-span-2" data-id="element-746" data-mp="3utz8rrr4ujmo">
                    <input type="text" value={formData.street} onChange={e => handleField('street', e.target.value)} className={inputClass(false)} placeholder="123 High Street" data-id="element-747" data-mp="3pu37i09tl6at" />
                  </Field>
                  <Field label="Line 2" className="sm:col-span-2" data-id="element-748" data-mp="hl3ae21dzn59">
                    <input type="text" value={formData.line2} onChange={e => handleField('line2', e.target.value)} className={inputClass(false)} placeholder="Suite, building, floor (optional)" data-id="element-749" data-mp="cnupvusrn70u" />
                  </Field>
                  <Field label="City" data-id="element-750" data-mp="1dmfrqxnx7mnm">
                    <input type="text" value={formData.city} onChange={e => handleField('city', e.target.value)} className={inputClass(false)} placeholder="London" data-id="element-751" data-mp="1hlyabms9rlna" />
                  </Field>
                  <Field label="County" data-id="element-752" data-mp="1wka5tq3woocv">
                    <input type="text" value={formData.county} onChange={e => handleField('county', e.target.value)} className={inputClass(false)} placeholder="Greater London" data-id="element-753" data-mp="1wheb0ancufsn" />
                  </Field>
                  <Field label="Postcode" className="sm:col-span-2" data-id="element-754" data-mp="2fa8g8cv6etjo">
                    <input type="text" value={formData.postcode} onChange={e => handleField('postcode', e.target.value)} className={inputClass(false)} placeholder="EC1A 1BB" data-id="element-755" data-mp="2f7vr9fr03ix4" />
                  </Field>
                </div>
              </section>

              {/* Contact section — unchanged */}
              <section className="space-y-4" data-id="element-756" data-mp="3lrf0ap41ir9m">
                <SectionHeader title="Contact" icon={<Phone className="w-4 h-4" data-id="element-758" data-mp="2y0st101v1b0o" />} data-id="element-757" data-mp="3lrooq89fsqd7" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-759" data-mp="2t2hhmkvx2sgc">
                  <Field label="Phone" data-id="element-760" data-mp="2t4c5o82hj0on">
                    <div className="relative" data-id="element-761" data-mp="372s83mtbe50z">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-762" data-mp="373tswj04u5vw" />
                      <input type="tel" value={formData.phone} onChange={e => handleField('phone', e.target.value)} className={`${inputClass(false)} pl-9`} placeholder="(555) 000-0000" data-id="element-763" data-mp="32zdrov1grmn0" />
                    </div>
                  </Field>
                  <Field label="Mobile" data-id="element-764" data-mp="utttmystu3tj">
                    <div className="relative" data-id="element-765" data-mp="fvsza97lk01i">
                      <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-766" data-mp="fx30365u3mbu" />
                      <input type="tel" value={formData.mobile} onChange={e => handleField('mobile', e.target.value)} className={`${inputClass(false)} pl-9`} placeholder="(555) 000-0000" data-id="element-767" data-mp="kve8rdke7qsh" />
                    </div>
                  </Field>
                  <Field label="Fax" data-id="element-768" data-mp="13sozg9rylbwb">
                    <div className="relative" data-id="element-769" data-mp="13rnnm334cldj">
                      <Printer className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-770" data-mp="1rj2odrf8yumo" />
                      <input type="tel" value={formData.fax} onChange={e => handleField('fax', e.target.value)} className={`${inputClass(false)} pl-9`} placeholder="(555) 000-0000" data-id="element-771" data-mp="1wb2uy2ptols8" />
                    </div>
                  </Field>
                  <Field label="Email" error={errors.email} data-id="element-772" data-mp="1s6s1ytddf70l">
                    <div className="relative" data-id="element-773" data-mp="1s8cy2nfc2y0k">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-774" data-mp="1n7fce0v47lub" />
                      <input type="email" value={formData.email} onChange={e => handleField('email', e.target.value)} className={`${inputClass(!!errors.email)} pl-9`} placeholder="email@example.com" data-id="element-775" data-mp="1ife4ss8w6jam" />
                    </div>
                  </Field>
                </div>
              </section>
            </form> : activeTab === 'notes' ? <NotesPanel notes={notes} newNote={newNote} setNewNote={setNewNote} onSubmit={handleSubmitNote} canAdd={!!onAddNote} data-id="element-776" data-mp="3b3vbfu4au2jq" /> : activeTab === 'contacts' ? <ContactsPanel contacts={contacts} onAdd={onAddContact} onToggleKeyContact={onToggleKeyContact} canAdd={!!onAddContact} data-id="element-777" data-mp="3l19nlhj6w568" /> : <ProjectsPanel projects={projects} data-id="element-778" data-mp="2xd9ysn5fuoua" />}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3" data-id="element-779" data-mp="31fdejnz54fld">
          <div data-id="element-780" data-mp="366ddszvqf9r1">
            {isEdit && isAdmin && onDelete && <button type="button" onClick={() => setConfirmDelete(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-781" data-mp="367ogewdfybgk">
                <Trash2 className="w-4 h-4" data-id="element-782" data-mp="172qh6obt8kqi" />
                Delete
              </button>}
          </div>
          <div className="flex items-center gap-3" data-id="element-783" data-mp="12586ase8fvfv">
            {showForm ? <>
                <button type="button" onClick={handleCancelEdit} className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm" data-id="element-784" data-mp="1gybc62igokqr">
                  Cancel
                </button>
                <button type="submit" form="client-form" className="px-5 py-2.5 text-sm font-medium text-white bg-[#6A9A24] hover:bg-[#587F1D] rounded-lg transition-colors shadow-sm" data-id="element-785" data-mp="1zqm8temivmgf">
                  Save
                </button>
              </> : <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm" data-id="element-786" data-mp="24r9xvur6xt80">
                Close
              </button>}
          </div>
        </div>
      </motion.aside>

      {/* Delete confirmation modal */}
      <AnimatePresence data-id="element-787" data-mp="2jaumyhhdwefa">
        {confirmDelete && onDelete && <DeleteConfirmDialog clientName={client?.name || 'this client'} onCancel={() => setConfirmDelete(false)} onConfirm={() => {
        setConfirmDelete(false);
        onDelete();
      }} data-id="element-788" data-mp="24mqdm4481f96" />}
      </AnimatePresence>
    </div>;
}
// --- Client Details View (read-only) ---
function ClientDetailsView({
  client
}: {
  client: Client;
}) {
  const formatDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }) : '—';
  const addressLines = [client.street, client.line2, [client.city, client.county].filter(Boolean).join(', '), client.postcode].filter(line => line && line.trim().length > 0);
  return <div className="p-6 space-y-6" data-id="element-789" data-mp="1wz7svnhzmi83">
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-790" data-mp="220dopu6y2llu">
        <StatBlock icon={<Briefcase className="w-4 h-4 text-slate-400" data-id="element-792" data-mp="26yypbuq3z7at" />} label="Projects" value={String(client.projectCount)} data-id="element-791" data-mp="2215kb50jkjc4" />
        <StatBlock icon={<DollarSign className="w-4 h-4 text-slate-400" data-id="element-794" data-mp="2bw791zqwnje6" />} label="Total Revenue" value={formatCurrency(client.totalRevenue)} data-id="element-793" data-mp="2bx07mdfpn2l8" />
        <StatBlock icon={<Calendar className="w-4 h-4 text-slate-400" data-id="element-796" data-mp="2kp264667y8sv" />} label="Last Project" value={formatDate(client.lastProjectDate)} data-id="element-795" data-mp="2kqvfvuc4p953" />
        <StatBlock icon={<Users className="w-4 h-4 text-slate-400" data-id="element-798" data-mp="2unqbo5h0ovfo" />} label="Client Since" value={formatDate(client.dateCreated)} data-id="element-797" data-mp="2uoja8j5toemq" />
      </div>

      {/* Details */}
      <ViewSection title="Details" data-id="element-799" data-mp="2ztn1oncs8glu">
        <ViewRow label="Ref" value={client.ref} mono data-id="element-800" data-mp="2zslp2eg4bm20" />
        <ViewRow label="Name" value={client.name} data-id="element-801" data-mp="2uuahkadapnmy" />
      </ViewSection>

      {/* Address */}
      <ViewSection title="Address" icon={<MapPin className="w-4 h-4" data-id="element-803" data-mp="2qtgtngzus8u2" />} data-id="element-802" data-mp="2qsovpzis9z0m">
        {addressLines.length > 0 ? <div className="text-sm text-slate-700 leading-relaxed" data-id="element-804" data-mp="3nioeemsnedwx">
            {addressLines.map((line, i) => <div key={i} data-id="element-805" data-mp="3ijl70vp73vls" data-mpi={i}>{line}</div>)}
          </div> : <p className="text-sm text-slate-400 italic" data-id="element-806" data-mp="3dswi9o4whcqc">No address on file.</p>}
      </ViewSection>

      {/* Contact */}
      <ViewSection title="Contact" icon={<Phone className="w-4 h-4" data-id="element-808" data-mp="39pgn6w8hbkfr" />} data-id="element-807" data-mp="39qi0z8gvql16">
        <ContactRow icon={<Phone className="w-3.5 h-3.5 text-slate-400" data-id="element-810" data-mp="a533n7a7948i" />} label="Phone" value={client.phone} href={client.phone ? `tel:${client.phone}` : undefined} data-id="element-809" data-mp="a4l1ju0uvvso" />
        <ContactRow icon={<Smartphone className="w-3.5 h-3.5 text-slate-400" data-id="element-812" data-mp="1zloyzh9gpgvl" />} label="Mobile" value={client.mobile} href={client.mobile ? `tel:${client.mobile}` : undefined} data-id="element-811" data-mp="1zkx5cbzfdt7y" />
        <ContactRow icon={<Printer className="w-3.5 h-3.5 text-slate-400" data-id="element-814" data-mp="1ptj7e8n4h1gx" />} label="Fax" value={client.fax} data-id="element-813" data-mp="1puua054u036g" />
        <ContactRow icon={<Mail className="w-3.5 h-3.5 text-slate-400" data-id="element-816" data-mp="1gtnqroasse9i" />} label="Email" value={client.email} href={client.email ? `mailto:${client.email}` : undefined} data-id="element-815" data-mp="1guyuxp85no15" />
      </ViewSection>
    </div>;
}
function StatBlock({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="border border-slate-200 rounded-lg p-3 bg-white" data-id="element-817" data-mp="1bxfh2qfddd9q">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium uppercase tracking-wide mb-1" data-id="element-818" data-mp="31b25jmluie8m">
        {icon}
        {label}
      </div>
      <div className="text-base font-bold text-slate-900 tabular-nums truncate" data-id="element-819" data-mp="ulqnruc0afx5">
        {value}
      </div>
    </div>;
}
function ViewSection({
  title,
  icon,
  children
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <section className="space-y-3" data-id="element-820" data-mp="14dwj9e1gvfh3">
      <div className="flex items-center gap-2 pb-2 border-b border-slate-100" data-id="element-821" data-mp="zl3behapbr0s">
        {icon && <span className="text-[#14466B]" data-id="element-822" data-mp="1dc8fpwp0l7hg">{icon}</span>}
        <h3 className="text-sm font-semibold text-slate-900" data-id="element-823" data-mp="1dayif9pzk3bv">{title}</h3>
      </div>
      <div className="space-y-2" data-id="element-824" data-mp="19amuno2btmw6">{children}</div>
    </section>;
}
function ViewRow({
  label,
  value,
  icon,
  mono
}: {
  label: string;
  value?: string;
  icon?: React.ReactNode;
  mono?: boolean;
}) {
  return <div className="flex items-start justify-between gap-4 py-1.5" data-id="element-825" data-mp="1i9rt6udw1g8c">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide flex-shrink-0 pt-0.5" data-id="element-826" data-mp="3p33oztuesskg">
        {label}
      </span>
      <div className="flex items-center gap-1.5 text-right min-w-0" data-id="element-827" data-mp="3k5c1rd4pluml">
        {icon}
        <span className={`text-sm text-slate-800 truncate ${mono ? 'tabular-nums font-medium' : ''}`} data-id="element-828" data-mp="3lk4xobmyzaqm">
          {value || '—'}
        </span>
      </div>
    </div>;
}
function ContactRow({
  icon,
  label,
  value,
  href
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  href?: string;
}) {
  const hasValue = !!value && value.trim().length > 0;
  return <div className="flex items-start justify-between gap-4 py-1.5" data-id="element-829" data-mp="32vggo0rlxpk4">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide flex-shrink-0 pt-0.5" data-id="element-830" data-mp="37s6x4p7bqn62">
        {label}
      </span>
      <div className="flex items-center gap-1.5 text-right min-w-0" data-id="element-831" data-mp="3cqrvsk6xgyse">
        {icon}
        {hasValue ? href ? <a href={href} className="text-sm text-[#14466B] hover:underline truncate" data-id="element-832" data-mp="2k3f9mb4oiszm">
              {value}
            </a> : <span className="text-sm text-slate-800 truncate" data-id="element-833" data-mp="2p0e0mkt1qjtl">{value}</span> : <span className="text-sm text-slate-400 italic" data-id="element-834" data-mp="1659snio380ew">Not provided</span>}
      </div>
    </div>;
}
// --- Tab Button ---
function TabButton({
  active,
  onClick,
  icon,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return <button type="button" onClick={onClick} className={`relative flex items-center gap-2 px-3 py-3 text-sm font-medium transition-colors ${active ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} aria-selected={active} role="tab" data-id="element-835" data-mp="n8i6n1h5v775">
      {icon}
      {label}
      {typeof count === 'number' && count > 0 && <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${active ? 'bg-[#14466B] text-white' : 'bg-slate-200 text-slate-700'}`} data-id="element-836" data-mp="9ioby2iz5spi">
          {count}
        </span>}
      {active && <motion.div layoutId="drawer-tab-indicator" className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#14466B]" data-id="element-837" data-mp="3lzxy9rfd0k13" />}
    </button>;
}
// --- Notes Panel ---
function NotesPanel({
  notes,
  newNote,
  setNewNote,
  onSubmit,
  canAdd
}: {
  notes: Note[];
  newNote: string;
  setNewNote: (v: string) => void;
  onSubmit: () => void;
  canAdd: boolean;
}) {
  const formatRelative = (iso: string) => {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="flex flex-col h-full" data-id="element-838" data-mp="2h0ra2qxfq8hb">
      <div className="flex-1 p-6 space-y-3" data-id="element-839" data-mp="2gzpzenkbzo04">
        {notes.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-840" data-mp="2c2fyobrhrpwz">
            <StickyNote className="w-10 h-10 text-slate-300 mb-3" data-id="element-841" data-mp="2lrzjhmyrltuh" />
            <h3 className="text-sm font-medium text-slate-900" data-id="element-842" data-mp="2luvhfbam4iix">No notes yet</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xs" data-id="element-843" data-mp="1y36w4ew7kubh">
              Add a note below to keep track of conversations, decisions, or
              reminders about this client.
            </p>
          </div> : notes.map(note => <article key={note.id} className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-300 transition-colors" data-id="element-844" data-mp="231rt85g5yxvp">
              <header className="flex items-center justify-between mb-2" data-id="element-845" data-mp="3ehggkxxbxyx1">
                <div className="flex items-center gap-2 min-w-0" data-id="element-846" data-mp="3ejj5tfqnro0p">
                  <div className="w-7 h-7 rounded-full bg-[#14466B] text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0" data-id="element-847" data-mp="1bw44sub59h0o">
                    {note.author.split(' ').map(s => s[0]).slice(0, 2).join('')}
                  </div>
                  <span className="text-sm font-medium text-slate-900 truncate" data-id="element-848" data-mp="1visbs5c9jgez">
                    {note.author}
                  </span>
                </div>
                <time className="text-xs text-slate-400 tabular-nums flex-shrink-0" dateTime={note.createdAt} title={new Date(note.createdAt).toLocaleString()} data-id="element-849" data-mp="1ziapag1r8p7s">
                  {formatRelative(note.createdAt)}
                </time>
              </header>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-id="element-850" data-mp="f48zruag7wlm">
                {note.body}
              </p>
            </article>)}
      </div>

      {canAdd && <div className="sticky bottom-0 p-6 pt-4 border-t border-slate-100 bg-white" data-id="element-851" data-mp="2uaw6dyvomtq">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2" data-id="element-852" data-mp="3u0c1e5w5wm7g">
            Add a note
          </label>
          <div className="space-y-2" data-id="element-853" data-mp="3p2alftu19307">
            <textarea value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }} rows={3} placeholder="Write something for the team to reference later..." className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 resize-none" data-id="element-854" data-mp="3oz4vkcdkxx3f" />
            <div className="flex items-center justify-between" data-id="element-855" data-mp="1w4tcb1rclogl">
              <span className="text-xs text-slate-400" data-id="element-856" data-mp="1w7603vjset1k">
                ⌘/Ctrl + Enter to post
              </span>
              <button type="button" onClick={onSubmit} disabled={!newNote.trim()} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] text-white text-sm font-medium hover:bg-[#587F1D] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-857" data-mp="1mf9pxm4ffcgz">
                <Send className="w-3.5 h-3.5" data-id="element-858" data-mp="2a2rk1ob5ymn4" />
                Add note
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
// --- Contacts Panel ---
function ContactsPanel({
  contacts,
  onAdd,
  onToggleKeyContact,
  canAdd
}: {
  contacts: Contact[];
  onAdd?: (c: Omit<Contact, 'id'>) => void;
  onToggleKeyContact?: (contactId: string) => void;
  canAdd: boolean;
}) {
  const [form, setForm] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    mobile: '',
    isPrimary: false,
    isKeyContact: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAdd, setShowAdd] = useState(false);
  const handleField = (key: keyof typeof form, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    if (typeof value === 'string' && errors[key]) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onAdd) return;
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) newErrors.email = 'Enter a valid email';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onAdd({
      name: form.name.trim(),
      role: form.role.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      isPrimary: form.isPrimary || undefined,
      isKeyContact: form.isKeyContact || undefined
    });
    setForm({
      name: '',
      role: '',
      email: '',
      phone: '',
      mobile: '',
      isPrimary: false,
      isKeyContact: false
    });
    setShowAdd(false);
  };
  const getInitials = (name: string) => name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return <div className="flex flex-col" data-id="element-859" data-mp="2ne4ea9yzphi">
      <div className="p-6 space-y-3" data-id="element-860" data-mp="3uokh3gdk4sc9">
        {contacts.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-861" data-mp="cjh6xh8qhq4z">
            <Users className="w-10 h-10 text-slate-300 mb-3" data-id="element-862" data-mp="7lpf0n83a40s" />
            <h3 className="text-sm font-medium text-slate-900" data-id="element-863" data-mp="r6fxzb3oiij6">
              No contacts yet
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xs" data-id="element-864" data-mp="w5sqoch8ejto">
              Add named people from this client's team — primary contact,
              accounts, project leads, and more.
            </p>
          </div> : contacts.map(c => <article key={c.id} className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-300 transition-colors" data-id="element-865" data-mp="m8wk4caw87rr">
              <div className="flex items-start gap-3" data-id="element-866" data-mp="d7q0hucy6gua">
                <div className="w-10 h-10 rounded-full bg-[#14466B] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0" data-id="element-867" data-mp="3unob5hngou7k">
                  {getInitials(c.name)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-868" data-mp="3ln9puiahzf42">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-869" data-mp="3loksufw4ciu4">
                    <h4 className="text-sm font-semibold text-slate-900 truncate" data-id="element-870" data-mp="3pnafz06yleqy">
                      {c.name}
                    </h4>
                    {c.isPrimary && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-700" data-id="element-871" data-mp="3dmk6uc1x0t9r">
                        <Star className="w-2.5 h-2.5 fill-current" data-id="element-872" data-mp="33w93orq22o3x" />
                        Primary
                      </span>}
                    {c.isKeyContact && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700" data-id="element-873" data-mp="2uvay44zyy8j8">
                        <Star className="w-2.5 h-2.5 fill-current" data-id="element-874" data-mp="2pxrh4xbvzhnl" />
                        Key contact
                      </span>}
                  </div>
                  {c.role && <p className="text-xs text-slate-500 mt-0.5" data-id="element-875" data-mp="j5gw018gypso">{c.role}</p>}
                  <div className="mt-2 space-y-1" data-id="element-876" data-mp="rmhaafgggr39">
                    {c.email && <ContactLine icon={<Mail className="w-3.5 h-3.5 text-slate-400" data-id="element-878" data-mp="vnavv26fdtsz" />} value={c.email} href={`mailto:${c.email}`} data-id="element-877" data-mp="rkoc7ojwrf6v" />}
                    {c.phone && <ContactLine icon={<Phone className="w-3.5 h-3.5 text-slate-400" data-id="element-880" data-mp="8o4y0ku8z13g" />} value={c.phone} href={`tel:${c.phone}`} data-id="element-879" data-mp="15d4bqfdlov31" />}
                    {c.mobile && <ContactLine icon={<Smartphone className="w-3.5 h-3.5 text-slate-400" data-id="element-882" data-mp="mno5dbf8i8ep" />} value={c.mobile} href={`tel:${c.mobile}`} data-id="element-881" data-mp="hmqfecnzga2n" />}
                  </div>
                  {onToggleKeyContact && <button type="button" onClick={() => onToggleKeyContact(c.id)} className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${c.isKeyContact ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'text-slate-500 hover:bg-slate-100 hover:text-[#14466B]'}`} aria-pressed={c.isKeyContact} data-id="element-883" data-mp="18e5tkdj6jy8x">
                      <Star className={`h-3.5 w-3.5 ${c.isKeyContact ? 'fill-current' : ''}`} data-id="element-884" data-mp="1dea8hz4fwkx1" />
                      {c.isKeyContact ? 'Key contact' : 'Mark as key contact'}
                    </button>}
                </div>
              </div>
            </article>)}
      </div>

      {canAdd && <div className="px-6 pb-6" data-id="element-885" data-mp="zfu3ccm872h0">
          {!showAdd ? <button type="button" onClick={() => setShowAdd(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:border-[#14466B] hover:text-[#14466B] hover:bg-slate-50 transition-colors" data-id="element-886" data-mp="um9128dlnmc0">
              <UserPlus className="w-4 h-4" data-id="element-887" data-mp="2ic5gu1fn9y9c" />
              Add contact
            </button> : <form onSubmit={handleSubmit} className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3" data-id="element-888" data-mp="23nsr3m75hbna">
              <div className="flex items-center justify-between" data-id="element-889" data-mp="28mvruug3kvpg">
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide" data-id="element-890" data-mp="2wd86eo5ulqp0">
                  New contact
                </h4>
                <button type="button" onClick={() => {
            setShowAdd(false);
            setErrors({});
          }} className="p-1 text-slate-400 hover:text-slate-600 rounded-full transition-colors" aria-label="Cancel" data-id="element-891" data-mp="318x9h1h2tpq2">
                  <X className="w-3.5 h-3.5" data-id="element-892" data-mp="1fy6vf8pzlek1" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-893" data-mp="27m19vl46tc6a">
                <Field label="Name" required error={errors.name} data-id="element-894" data-mp="27j5d4042stjf">
                  <input type="text" value={form.name} onChange={e => handleField('name', e.target.value)} className={inputClass(!!errors.name)} placeholder="e.g. Jane Doe" data-id="element-895" data-mp="22kvgdl2pdkvp" />
                </Field>
                <Field label="Role" data-id="element-896" data-mp="2qe3qiwgtwsgj">
                  <input type="text" value={form.role} onChange={e => handleField('role', e.target.value)} className={inputClass(false)} placeholder="e.g. Project Manager" data-id="element-897" data-mp="2m9w3d3q0rkkf" />
                </Field>
                <Field label="Email" error={errors.email} className="sm:col-span-2" data-id="element-898" data-mp="3a11rs7a0zl6c">
                  <div className="relative" data-id="element-899" data-mp="3ckelv54dw60j">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-900" data-mp="3clg6o1b7c6vg" />
                    <input type="email" value={form.email} onChange={e => handleField('email', e.target.value)} className={`${inputClass(!!errors.email)} pl-9`} placeholder="name@example.com" data-id="element-901" data-mp="3hkapv4d2zxg3" />
                  </div>
                </Field>
                <Field label="Phone" data-id="element-902" data-mp="4cfz195575bn">
                  <div className="relative" data-id="element-903" data-mp="4beqbbblmux3">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-904" data-mp="93y3462gpm0u" />
                    <input type="tel" value={form.phone} onChange={e => handleField('phone', e.target.value)} className={`${inputClass(false)} pl-9`} placeholder="(555) 000-0000" data-id="element-905" data-mp="e2j1dzy5lvmn" />
                  </div>
                </Field>
                <Field label="Mobile" data-id="element-906" data-mp="1prbhug3tau32">
                  <div className="relative" data-id="element-907" data-mp="1tupvx186x0dv">
                    <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-908" data-mp="1tvrj242hddby" />
                    <input type="tel" value={form.mobile} onChange={e => handleField('mobile', e.target.value)} className={`${inputClass(false)} pl-9`} placeholder="(555) 000-0000" data-id="element-909" data-mp="1fw9thmhd1w1j" />
                  </div>
                </Field>
              </div>

              <div className="space-y-2" data-id="element-910" data-mp="1ysrqojezao56">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-911" data-mp="23se2cof5rwbv">
                  <input type="checkbox" checked={form.isPrimary} onChange={e => handleField('isPrimary', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-[#14466B] focus:ring-blue-200" data-id="element-912" data-mp="23qb3dew12sv0" />
                  <span className="text-sm text-slate-700" data-id="element-913" data-mp="krln0v1otgkk">Mark as primary contact</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-914" data-mp="tm08xggq3pcn">
                  <input type="checkbox" checked={form.isKeyContact} onChange={e => handleField('isKeyContact', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-[#14466B] focus:ring-blue-200" data-id="element-915" data-mp="tl8ggeifa7ql" />
                  <span className="text-sm text-slate-700" data-id="element-916" data-mp="3t3hpv3s7msp4">Mark as key contact</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1" data-id="element-917" data-mp="5vxl5cjud5ju">
                <button type="button" onClick={() => {
            setShowAdd(false);
            setErrors({});
          }} className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-white rounded-lg transition-colors" data-id="element-918" data-mp="5ys3myjaogam">
                  Cancel
                </button>
                <button type="submit" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#6A9A24] text-white text-sm font-medium hover:bg-[#587F1D] transition-colors shadow-sm" data-id="element-919" data-mp="jvg34nco2jv7">
                  <UserPlus className="w-3.5 h-3.5" data-id="element-920" data-mp="3sdck6g5s16v8" />
                  Add contact
                </button>
              </div>
            </form>}
        </div>}
    </div>;
}
function ContactLine({
  icon,
  value,
  href
}: {
  icon: React.ReactNode;
  value: string;
  href?: string;
}) {
  return <div className="flex items-center gap-1.5 text-xs" data-id="element-921" data-mp="9zd0lgdwkj7q">
      {icon}
      {href ? <a href={href} className="text-[#14466B] hover:underline truncate" data-id="element-922" data-mp="38pmz0rsci4uz">
          {value}
        </a> : <span className="text-slate-700 truncate" data-id="element-923" data-mp="3dn4zdtsarbrd">{value}</span>}
    </div>;
}
// --- Projects Panel ---
function ProjectsPanel({
  projects
}: {
  projects: Project[];
}) {
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const totalValue = projects.reduce((sum, p) => sum + p.value, 0);
  const activeCount = projects.filter(p => p.status === 'active').length;
  if (projects.length === 0) {
    return <div className="p-6" data-id="element-924" data-mp="2n7dztnklbl86">
        <div className="flex flex-col items-center justify-center text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-925" data-mp="2n7mhdsrnrepp">
          <Briefcase className="w-10 h-10 text-slate-300 mb-3" data-id="element-926" data-mp="2dh1g1xklnw33" />
          <h3 className="text-sm font-medium text-slate-900" data-id="element-927" data-mp="28h6tu7brruod">
            No projects yet
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xs" data-id="element-928" data-mp="24ftl9phalddr">
            Projects linked to this client will appear here. Create one from the
            Jobs tab to get started.
          </p>
        </div>
      </div>;
  }
  return <div className="p-6 space-y-4" data-id="element-929" data-mp="3ednwa70d6qs">
      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3" data-id="element-930" data-mp="7f774q9i9xdc">
        <div className="bg-slate-50 border border-slate-100 rounded-lg p-3" data-id="element-931" data-mp="7dw44snvwtna">
          <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide" data-id="element-932" data-mp="3prvm5o04198o">
            Active
          </div>
          <div className="text-lg font-bold text-slate-900 tabular-nums" data-id="element-933" data-mp="3upne2i0r8vcv">
            {activeCount}
            <span className="text-slate-400 font-normal text-sm" data-id="element-934" data-mp="3fwlk509pp1uw">
              {' '}
              / {projects.length}
            </span>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-lg p-3" data-id="element-935" data-mp="36v47xszfdkbh">
          <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide" data-id="element-936" data-mp="3bsdw75b2vpxq">
            Total Value
          </div>
          <div className="text-lg font-bold text-slate-900 tabular-nums" data-id="element-937" data-mp="152z87vbopiq7">
            {formatCurrency(totalValue)}
          </div>
        </div>
      </div>

      {/* Project cards */}
      <div className="space-y-3" data-id="element-938" data-mp="1k6isc6xbl20x">
        {projects.map(p => <ProjectCard key={p.id} project={p} formatDate={formatDate} data-id="element-939" data-mp="1f6mpib0czgmy" />)}
      </div>
    </div>;
}
function ProjectCard({
  project,
  formatDate
}: {
  project: Project;
  formatDate: (iso: string) => string;
}) {
  const statusConfig: Record<ProjectStatus, {
    label: string;
    icon: React.ReactNode;
    classes: string;
  }> = {
    active: {
      label: 'Active',
      icon: <Loader2 className="w-3 h-3" data-id="element-940" data-mp="1xxdt0vxo0ctf" />,
      classes: 'bg-emerald-50 text-emerald-700 border-emerald-100'
    },
    completed: {
      label: 'Completed',
      icon: <CheckCircle2 className="w-3 h-3" data-id="element-941" data-mp="2buj5pervw9xn" />,
      classes: 'bg-slate-100 text-slate-600 border-slate-200'
    },
    on_hold: {
      label: 'On hold',
      icon: <PauseCircle className="w-3 h-3" data-id="element-942" data-mp="hjnltgb918fk" />,
      classes: 'bg-amber-50 text-amber-700 border-amber-100'
    }
  };
  const s = statusConfig[project.status];
  return <article className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-300 transition-colors" data-id="element-943" data-mp="2cm0td954xrl2">
      <div className="flex items-start justify-between gap-3 mb-2" data-id="element-944" data-mp="2hkc6ptun2k81">
        <div className="min-w-0 flex-1" data-id="element-945" data-mp="2hmos6gzlvcpu">
          <div className="flex items-center gap-2 mb-0.5" data-id="element-946" data-mp="2mix5dou8svud">
            <span className="text-xs font-medium text-slate-400 tabular-nums" data-id="element-947" data-mp="2rj1mnh2z5uln">
              {project.ref}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-slate-900 truncate" data-id="element-948" data-mp="2vjllcp37569w">
            {project.name}
          </h4>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${s.classes} flex-shrink-0`} data-id="element-949" data-mp="30hwusypkxerl">
          {s.icon}
          {s.label}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100" data-id="element-950" data-mp="3f8m7y6mlp3y3">
        <div className="flex items-center gap-1.5 text-xs text-slate-500" data-id="element-951" data-mp="3ja7g5eo6wu1y">
          <Calendar className="w-3.5 h-3.5" data-id="element-952" data-mp="3j84s306llazv" />
          <span data-id="element-953" data-mp="17ndqlzc2kmk2">
            {formatDate(project.startDate)}
            {project.endDate ? ` – ${formatDate(project.endDate)}` : ' – Present'}
          </span>
        </div>
        <div className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-954" data-mp="yle6gy5rd2cw">
          {formatCurrency(project.value)}
        </div>
      </div>
    </article>;
}
// --- Drawer helpers ---
function SectionHeader({
  title,
  icon
}: {
  title: string;
  icon?: React.ReactNode;
}) {
  return <div className="flex items-center gap-2 pb-2 border-b border-slate-100" data-id="element-955" data-mp="1mb8jbfq2uqx5">
      {icon && <span className="text-[#14466B]" data-id="element-956" data-mp="1hks5huich3a9">{icon}</span>}
      <h3 className="text-sm font-semibold text-slate-900" data-id="element-957" data-mp="1hjh2vy0my1kq">{title}</h3>
    </div>;
}
function Field({
  label,
  required,
  error,
  hint,
  className = '',
  children
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={`space-y-1.5 ${className}`} data-id="element-958" data-mp="259bdsa1e9g2c">
      <label className="block text-sm font-medium text-[#14466B]" data-id="element-959" data-mp="1dmjifje9yglg">
        {label}
        {required && '*'}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-slate-400" data-id="element-960" data-mp="1ifum5bnsih95">{''}</p>}
      {error && <p className="text-xs text-red-500" data-id="element-961" data-mp="14no8bw840mcv">{error}</p>}
    </div>;
}
function inputClass(hasError: boolean) {
  return `w-full px-4 py-2.5 rounded-lg border ${hasError ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'} focus:ring-2 outline-none transition-all text-sm text-slate-700`;
}
// --- Delete Confirmation Dialog ---
function DeleteConfirmDialog({
  clientName,
  onCancel,
  onConfirm
}: {
  clientName: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);
  return <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-id="element-962" data-mp="8egy2jyb8g3e">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-slate-900/50" onClick={onCancel} data-id="element-963" data-mp="4d527woueymr" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 8
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6" role="alertdialog" aria-modal="true" data-id="element-964" data-mp="3luo8ze1bl300">
        <div className="flex items-start gap-4" data-id="element-965" data-mp="2y5degeakc4ub">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-966" data-mp="2y4kgo2tl0pob">
            <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-967" data-mp="35v4f1mxrqlb1" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-968" data-mp="31ukh4h5dfdnu">
            <h3 className="text-base font-semibold text-slate-900" data-id="element-969" data-mp="3fmqw3zwsfelp">
              Delete client?
            </h3>
            <p className="text-sm text-slate-600 mt-1.5" data-id="element-970" data-mp="3anxi86dl9ll0">
              You're about to permanently delete{' '}
              <span className="font-medium text-slate-900" data-id="element-971" data-mp="2mxjp0cj9pgou">{clientName}</span>.
              This action cannot be undone, and any associated records will lose
              their link to this client.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-6" data-id="element-972" data-mp="2wxkb268iuawq">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors" data-id="element-973" data-mp="2rw4na6eylqan">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm" data-id="element-974" data-mp="1p4ttz032ya2v">
            Delete client
          </button>
        </div>
      </motion.div>
    </div>;
}
// --- Filter Popover ---
type FilterPopoverProps = {
  filterStatus: 'all' | 'active' | 'inactive';
  setFilterStatus: (v: 'all' | 'active' | 'inactive') => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
};
const SORT_OPTIONS: {
  label: string;
  field: SortField;
  order: SortOrder;
}[] = [{
  label: 'Client Name (A–Z)',
  field: 'name',
  order: 'asc'
}, {
  label: 'Client Name (Z–A)',
  field: 'name',
  order: 'desc'
}, {
  label: 'Date Created (Newest)',
  field: 'dateCreated',
  order: 'desc'
}, {
  label: 'Date Created (Oldest)',
  field: 'dateCreated',
  order: 'asc'
}, {
  label: 'Projects (Most)',
  field: 'projectCount',
  order: 'desc'
}, {
  label: 'Revenue (Highest)',
  field: 'totalRevenue',
  order: 'desc'
}, {
  label: 'Last Project (Recent)',
  field: 'lastProjectDate',
  order: 'desc'
}, {
  label: 'Last Project (Oldest)',
  field: 'lastProjectDate',
  order: 'asc'
}];
function FilterPopover({
  filterStatus,
  setFilterStatus,
  sortField,
  sortOrder,
  onSortChange
}: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);
  const activeCount = filterStatus !== 'all' ? 1 : 0;
  const currentSortKey = `${sortField}-${sortOrder}`;
  const clearAll = () => {
    setFilterStatus('all');
  };
  return <div ref={ref} className="relative" data-id="element-975" data-mp="t7nv6uk9ta8m">
      <button onClick={() => setOpen(v => !v)} aria-expanded={open} aria-haspopup="dialog" className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${open || activeCount > 0 ? 'bg-slate-100 border-slate-300 text-[#14466B]' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-976" data-mp="t5biowxa7s2y">
        <SlidersHorizontal className="w-4 h-4" data-id="element-977" data-mp="aad1dpher90k" />
        Filters
        {activeCount > 0 && <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#14466B] text-white text-[10px] font-semibold" data-id="element-978" data-mp="38wpm4us1kvbb">
            {activeCount}
          </span>}
      </button>

      <AnimatePresence data-id="element-979" data-mp="1fgcu60gkn8bs">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="absolute right-0 sm:right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 origin-top-right" role="dialog" data-id="element-980" data-mp="1fho0a6xhos62">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between" data-id="element-981" data-mp="hr9hwaimbcxs">
              <h3 className="text-sm font-semibold text-slate-900" data-id="element-982" data-mp="cthotd68lks0">
                Filters & Sort
              </h3>
              {activeCount > 0 && <button onClick={clearAll} className="text-xs text-[#14466B] font-medium hover:underline" data-id="element-983" data-mp="2oiei0fcfnjx3">
                  Clear
                </button>}
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-984" data-mp="2jp363zsrkcyb">
              {/* Status */}
              <div className="space-y-2" data-id="element-985" data-mp="25pkd6e8ix86l">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide" data-id="element-986" data-mp="25oiqfci5ax91">
                  Status
                </label>
                <div className="flex flex-wrap gap-1.5" data-id="element-987" data-mp="1wpngpqfw750z">
                  {([{
                value: 'all',
                label: 'All',
                dot: null
              }, {
                value: 'active',
                label: 'Active',
                dot: 'bg-emerald-500'
              }, {
                value: 'inactive',
                label: 'Inactive',
                dot: 'bg-slate-400'
              }] as const).map(opt => <button key={opt.value} onClick={() => setFilterStatus(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterStatus === opt.value ? 'bg-[#14466B] border-[#14466B] text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-988" data-mp="twt5yq6mf7pu">
                      {opt.dot && <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} data-id="element-989" data-mp="1ctb3xpc2c3uj" />}
                      {opt.label}
                    </button>)}
                </div>
              </div>

              {/* Sort */}
              <div className="space-y-2" data-id="element-990" data-mp="2cxka4teqcvpa">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide" data-id="element-991" data-mp="2htium5wxttkf">
                  Sort by
                </label>
                <div className="space-y-1" data-id="element-992" data-mp="2mvhuv6xdsad5">
                  {SORT_OPTIONS.map(opt => {
                const key = `${opt.field}-${opt.order}`;
                const isActive = key === currentSortKey;
                return <button key={key} onClick={() => onSortChange(opt.field, opt.order)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-blue-50 text-[#14466B] font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-993" data-mp="30sekx65odnsz">
                        <span data-id="element-994" data-mp="3jjefcmvxvptn">{opt.label}</span>
                        {isActive && <span className="w-2 h-2 rounded-full bg-[#14466B]" data-id="element-995" data-mp="8p8dsdvjgu9y" />}
                      </button>;
              })}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Pagination ---
type PaginationProps = {
  page: number;
  totalPages: number;
  pageSize: number;
  setPageSize: (n: number) => void;
  setPage: (n: number) => void;
  showingFrom: number;
  showingTo: number;
  total: number;
};
function Pagination({
  page,
  totalPages,
  pageSize,
  setPageSize,
  setPage,
  showingFrom,
  showingTo,
  total
}: PaginationProps) {
  const canPrev = page > 1;
  const canNext = page < totalPages;
  return <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100" data-id="element-996" data-mp="3r2g0lzi2xe5l">
      <div className="flex items-center gap-3 text-sm text-slate-600" data-id="element-997" data-mp="33bj1jnbdubbr">
        <span className="tabular-nums" data-id="element-998" data-mp="2ye0zmgvs8wd9">
          Showing{' '}
          <span className="font-semibold text-slate-900" data-id="element-999" data-mp="1dvzv5fpvtr1y">{showingFrom}</span>
          {'\u2013'}
          <span className="font-semibold text-slate-900" data-id="element-1000" data-mp="1inqc44jbrd2c">
            {showingTo}
          </span> of{' '}
          <span className="font-semibold text-slate-900" data-id="element-1001" data-mp="13z3l35kc4wva">{total}</span>
        </span>
        <div className="relative" data-id="element-1002" data-mp="18x5cqevtu4id">
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="pl-3 pr-8 py-1.5 rounded-lg border border-slate-200 bg-white appearance-none text-xs font-medium text-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all cursor-pointer" aria-label="Rows per page" data-id="element-1003" data-mp="18y6s2vjvld5w">
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n} data-id="element-1004" data-mp="c75utttsdtn4">
                {n} per page
              </option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" data-id="element-1005" data-mp="g99c4z34zsgb" />
        </div>
      </div>

      <div className="flex items-center gap-2" data-id="element-1006" data-mp="3bghh5grnoz18">
        <button onClick={() => canPrev && setPage(page - 1)} disabled={!canPrev} aria-label="Previous page" className={`p-2 rounded-lg border transition-colors ${canPrev ? 'border-slate-200 text-slate-700 hover:bg-slate-50' : 'border-slate-100 text-slate-300 cursor-not-allowed'}`} data-id="element-1007" data-mp="3gcopfg7iymmc">
          <ChevronLeft className="w-4 h-4" data-id="element-1008" data-mp="2spgm33tnpnmy" />
        </button>
        <span className="text-sm text-slate-600 tabular-nums px-2" data-id="element-1009" data-mp="2woz5tw9qrso7">
          Page <span className="font-semibold text-slate-900" data-id="element-1010" data-mp="31ok4s6oww10x">{page}</span> of{' '}
          <span className="font-semibold text-slate-900" data-id="element-1011" data-mp="31mh48sq4uphy">{totalPages}</span>
        </span>
        <button onClick={() => canNext && setPage(page + 1)} disabled={!canNext} aria-label="Next page" className={`p-2 rounded-lg border transition-colors ${canNext ? 'border-slate-200 text-slate-700 hover:bg-slate-50' : 'border-slate-100 text-slate-300 cursor-not-allowed'}`} data-id="element-1012" data-mp="36glcrgf1r5l5">
          <ChevronRight className="w-4 h-4" data-id="element-1013" data-mp="21gdp78pgxvzh" />
        </button>
      </div>
    </div>;
}